import "./index.css";
import { useEffect } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";
import LZString from "lz-string";

import UFOSegment from "@atlaskit/react-ufo/segment";
import type AnalyticsWebClient from "@atlassiansox/analytics-web-client";

import {
    WacAnalyticsClientProvider,
    WacAnalyticsListener
} from "@/analytics/wac-analytics";
import { SitesContextProvider } from "@/components/sites-provider";
import { UserContextProvider } from "@/components/user-context";
import { GupsProvider } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";

import { InitializeAutoAtlAnalytics } from "./analytics/atl-analytics";
import { InitUFOV1 } from "./analytics/ufo/initUFO";
import { TemplateCategory } from "./components/confluence/templates/template-categories-search";
import { CrossFlowProvider } from "./components/cross-flow";
import LanguageSelectorBanner from "./components/language-selector-banner/src";
import { AppPricingData } from "./contentful";
import {
    AvailableProductsResponse,
    BxpWebStargateClientProvider,
    ServerSideRequestStatus,
    UserProfile,
    UserProfileResponse
} from "./libraries/bxp-web-stargate-client/src";
import { GrowthUnifiedProfile } from "./libraries/growth-unified-profile-client";
import { AgileTutorial } from "./templates/agile-hub/tutorial-types";
import { AgileArticle, AgileOverview } from "./templates/agile-hub/types";
import { trackRouteUFOV1PageLoad } from "./ufo-v1";
import { Cookie } from "./utils";
import { AnchorContentsResponse } from "./utils/anchor-queries/get-anchor-contents";
import { checkFeatureFlagGate, featureFlags } from "./utils/feature-flags";
import { GridOverlayWrapper } from "./utils/grid-overlay";
import { useAnchorLocalStorage } from "./utils/local-storage-hook";
import { LocationProvider } from "./utils/location-provider";
import { LoggedInUserProvider } from "./utils/logged-in-user-provider";
import { OneTrustProvider } from "./utils/onetrust-provider";
import { PricingContent } from "./utils/pricing-queries/get-pricing-content";
import { SmartReCaptcha } from "./utils/smart-recaptcha";
import { SSRLoginStateProvider } from "./utils/ssr-login-state-provider";
import { UserLocale } from "./utils/useGeoCurrency";
import { parseRoute } from "./wac-routes";

import type {
    Template,
    TemplateCollection
} from "./content/templates/template.types";
import type { PreloadedQuery } from "react-relay";
import type { OperationType } from "relay-runtime";

export type LayoutProps = {
    analyticsWebClient: AnalyticsWebClient | null;
    location: Location;
    pricing?: AppPricingData;
    availableProducts?: AvailableProductsResponse;
    userProfile?: UserProfile;
    userProfileStatus?: ServerSideRequestStatus;
    userContext?: UserProfileResponse;
    userContextStatus?: ServerSideRequestStatus;
    loggedOutContext?: GrowthUnifiedProfile;
    loggedOutContextStatus?: ServerSideRequestStatus;
    countryCode?: UserLocale;
    templates?: Template[];
    agileArticle?: AgileArticle;
    agileOverview?: AgileOverview;
    tutorial?: AgileTutorial;
    partnerTemplates?: Template[];
    collections?: TemplateCollection[];
    categories?: TemplateCategory[];
    anchorContents?: AnchorContentsResponse;
    pricingContent?: PricingContent;
    persona?: string;
    teamType?: string;
    composition?: RootComponentInstance;
    isUniformPlayground?: boolean;
    keyword?: string;
    mlTemplateIds?: string[];
    anchor?: string;
    subAnchor?: string;
    referrer?: string;
    hasCloudSessionTokenCookie?: boolean;
    children: React.ComponentType<PageProps>;
    queryRef?: PreloadedQuery<OperationType>;
};

export type PageProps = {
    pricing?: AppPricingData;
    availableProducts?: AvailableProductsResponse;
    userProfile?: UserProfile;
    userProfileStatus?: ServerSideRequestStatus;
    userContext?: UserProfileResponse;
    userContextStatus?: ServerSideRequestStatus;
    loggedOutContext?: GrowthUnifiedProfile;
    loggedOutContextStatus?: ServerSideRequestStatus;
    countryCode?: UserLocale;
    templates?: Template[];
    agileArticle?: AgileArticle;
    agileOverview?: AgileOverview;
    tutorial?: AgileTutorial;
    partnerTemplates?: Template[];
    collections?: TemplateCollection[];
    categories?: TemplateCategory[];
    anchorContents?: AnchorContentsResponse;
    pricingContent?: PricingContent;
    persona?: string;
    teamType?: string;
    composition?: RootComponentInstance;
    isUniformPlayground?: boolean;
    keyword?: string;
    mlTemplateIds?: string[];
    anchor?: string;
    subAnchor?: string;
    referrer?: string;
    queryRef?: PreloadedQuery<OperationType>;
};

/* Temporary code for debugging purposes - will be removed */
function getExperimentKeyValue(query: string, key: string): string | null {
    if (!query.startsWith("?")) {
        query = "?" + query;
    }

    const params = new URLSearchParams(query);
    return params.get(key);
}

const logHandlePageShow = (
    event: PageTransitionEvent,
    analyticsWebClient: AnalyticsWebClient | null
) => {
    if (!analyticsWebClient) return;

    const featureGatesAnonIdUsedInSSR =
        window?.featureGateBootstrapValues.identifiers.analyticsAnonymousId;

    /*  We need two checks because:
        1. event.persisted works for pages that support bfcache
        2. performance.navigation check works for pages that don't support bfcache
        This hybrid approach ensures we get back/forward navigation regardless of bfcache support */
    const isBackForward =
        event.persisted ||
        (() => {
            try {
                const [entry] = performance.getEntriesByType("navigation");
                return entry && entry.type === "back_forward";
            } catch (perfError) {
                console.warn("Failed to check navigation type:", perfError);
                return false;
            }
        })();

    analyticsWebClient.sendScreenEvent({
        name: isBackForward ? "backForwardPageShow" : "nonBackForwardPageShow",
        attributes: {
            route: window.location.pathname,
            anonIdUsedInSSR: featureGatesAnonIdUsedInSSR,
            anonIdInAnalyticsClient: analyticsWebClient.getAnonymousId()
        }
    });
};

const X_EXPERIMENTS_KEY = "X-Experiments-Key";

const checkEdgeCacheKeyMismatch = (
    analyticsWebClient: AnalyticsWebClient | null,
    wacWebTrafficAAGateValue: boolean
) => {
    if (analyticsWebClient) {
        const xExperimentsKey = Cookie.readCookie(X_EXPERIMENTS_KEY);
        if (xExperimentsKey) {
            const decodedExperimentsKey = LZString.decompressFromBase64(
                xExperimentsKey.split("~")[1] as string
            );
            const evaluatedValueInExpKey = getExperimentKeyValue(
                decodedExperimentsKey,
                featureFlags.wacWebTrafficAAGate.featureFlagName
            );
            const featureGatesAnonIdUsedInSSR =
                window?.featureGateBootstrapValues.identifiers
                    .analyticsAnonymousId;

            if (
                evaluatedValueInExpKey != null &&
                String(wacWebTrafficAAGateValue) !== evaluatedValueInExpKey
            ) {
                analyticsWebClient.sendOperationalEvent({
                    action: "mismatched",
                    actionSubject: "featureGate",
                    source: "layout",
                    attributes: {
                        featureGateName:
                            featureFlags.wacWebTrafficAAGate.featureFlagName,
                        route: window.location.pathname,
                        anonIdUsedInSSR: featureGatesAnonIdUsedInSSR,
                        anonIdInAnalyticsClient:
                            analyticsWebClient.getAnonymousId(),
                        experimentKeyValue: evaluatedValueInExpKey,
                        featureGateValue: String(wacWebTrafficAAGateValue),
                        fallback: window.IS_STATIC ? "true" : "false"
                    }
                });
            }
        }
    }
};
/* */

export const Layout = (props: LayoutProps) => {
    const hasAutoAtlAnalytics =
        process.env["AUTO_ANALYTICS_ENABLED"] === "true";
    const pageViewProperties: { originProduct?: string } = {};
    const { locale, path } = parseRoute(location);
    useAnchorLocalStorage();
    // AA gate, purely to observe the traffic volume in Statsig
    /* Use Effect below is temporary code for debugging purposes - will be removed */
    // useEdgeKeyMismatchHook(props.analyticsWebClient, wacWebTrafficAAGateValue);
    const isBrowser = () => typeof window !== "undefined";
    if (isBrowser()) {
        trackRouteUFOV1PageLoad(path);
        pageViewProperties.originProduct =
            "wac" +
            (window.location.pathname === "/"
                ? ""
                : window.location.pathname.replace(new RegExp("/", "g"), "-"));
        checkFeatureFlagGate(featureFlags.wacWebUseEsModulesScripts);

        // Trigger exposure for the edge lambda cache product discovery feature flag that is checked server side
        checkFeatureFlagGate(featureFlags.edgeLambdaCacheProductDiscovery);
    }

    useEffect(() => {
        const wacWebTrafficAAGateValue = checkFeatureFlagGate(
            featureFlags.wacWebTrafficAAGate
        );
        if (window.location.pathname === "/fastshift/dashboard") {
            checkFeatureFlagGate(featureFlags.sxpEnableFastshiftPortal);
        }
        checkEdgeCacheKeyMismatch(
            props.analyticsWebClient,
            wacWebTrafficAAGateValue
        );
    }, [props.analyticsWebClient]);

    useEffect(() => {
        function handlePageShow(event: PageTransitionEvent) {
            logHandlePageShow(event, props.analyticsWebClient);
        }

        window.addEventListener("pageshow", handlePageShow);

        return () => {
            window.removeEventListener("pageshow", handlePageShow);
        };
    }, [props.analyticsWebClient]);

    return (
        <OneTrustProvider>
            {process.env["NODE_ENV"] !== "production" && <GridOverlayWrapper />}
            <WacAnalyticsClientProvider
                analyticsWebClient={props.analyticsWebClient}
                location={props.location}
            >
                <InitUFOV1 />
                <LocationProvider location={props.location}>
                    <UFOSegment name="wac">
                        <SmartReCaptcha>
                            <BxpWebStargateClientProvider
                                baseUrl={
                                    process.env[
                                        "NEXT_PUBLIC_API_PRIVATE_BASE_URL"
                                    ] as string
                                }
                            >
                                <SSRLoginStateProvider
                                    hasCloudSessionTokenCookie={
                                        props.hasCloudSessionTokenCookie ??
                                        false
                                    }
                                >
                                    <LoggedInUserProvider>
                                        <GupsProvider>
                                            <WacAnalyticsListener>
                                                <CrossFlowProvider
                                                    locale={locale}
                                                >
                                                    <SitesContextProvider>
                                                        <UserContextProvider
                                                            location={
                                                                props.location
                                                                    .href
                                                            }
                                                            referrer={
                                                                props.referrer
                                                            }
                                                            meResponse={
                                                                props.userProfile
                                                            }
                                                            meRequest={
                                                                props.userProfileStatus
                                                            }
                                                            userProfileResponse={
                                                                props.userContext
                                                            }
                                                            userProfileRequest={
                                                                props.userContextStatus
                                                            }
                                                            loggedOutContext={
                                                                props.loggedOutContext
                                                            }
                                                            loggedOutContextStatus={
                                                                props.loggedOutContextStatus
                                                            }
                                                        >
                                                            <props.children
                                                                {...props}
                                                            />
                                                        </UserContextProvider>
                                                    </SitesContextProvider>
                                                    {/**
                                                     * InitializeAutoAtlAnalytics is used to auto-capture GTM events (previously handled
                                                     * by the atl-analytics GTM wrapper). The contents of that wrapper have been ported into
                                                     * this repository.
                                                     *
                                                     * Note: this will only attach auto-analytics to elements that exist in the page
                                                     * when this component is mounted, so it needs to be the last component to render.
                                                     *
                                                     * In its current state, this will only pick up the components that existed on
                                                     * after the first render of the above Component.
                                                     *
                                                     * You can call Initializer.autoTrack() in a useEffect or useLayoutEffect
                                                     * to attach listeners to any new buttons/links/etc. that were added dynamically.
                                                     */}
                                                    {hasAutoAtlAnalytics && (
                                                        <InitializeAutoAtlAnalytics
                                                            pageViewProperties={
                                                                pageViewProperties
                                                            }
                                                        />
                                                    )}
                                                </CrossFlowProvider>
                                            </WacAnalyticsListener>
                                        </GupsProvider>
                                    </LoggedInUserProvider>
                                </SSRLoginStateProvider>
                            </BxpWebStargateClientProvider>
                        </SmartReCaptcha>
                    </UFOSegment>
                </LocationProvider>
            </WacAnalyticsClientProvider>
        </OneTrustProvider>
    );
};
